import { CONTENT_TYPES, IMAGE_MODE } from '../constants/contentTypes';
import { getContentImageByMode } from './imageUtils';

/**
 * Utilities for BROADCAST and EPISODE zapping.
 *
 * The player's zapping carousel shows the contents of the content list the
 * content was opened from, so that origin (section + component) needs to be
 * carried from the carousel to the player.
 */

/** Components that represent a zappable content list. */
const ZAPPABLE_COMPONENT_TYPES = ['carousel', 'gallery_wall'];

/**
 * Builds a widget's zapping origin to travel in the router's state.
 *
 * @param {Object} widget section widget
 * @returns {Object|null} { sectionId, widgetId, itemsURL, title } or null
 */
export const buildZappingOrigin = (widget) => {
  if (!widget || !ZAPPABLE_COMPONENT_TYPES.includes(widget.componentType)) {
    return null;
  }

  if (!widget.id || (!widget.section && !widget.itemsURL)) {
    return null;
  }

  return {
    sectionId: widget.section || null,
    widgetId: widget.id,
    itemsURL: widget.itemsURL || null,
    title: widget.componentParameters?.title || '',
  };
};

/**
 * Number of channels requested for the zapping carousel.
 * The component may be configured to show fewer items than the content list
 * has, but zapping always shows all of them.
 */
export const ZAPPING_PAGE_SIZE = 25;

/**
 * Trims the channels to the category chosen in the EPG grid.
 *
 * Mirrors the grid's filter (`EPGGrid/hooks.js`): compares case-insensitively
 * and, with no category chosen, returns the entire list.
 *
 * If the channel on air doesn't belong to the category it's left out of the
 * carousel: zapping shows the same thing as the grid, with no card marked as
 * "Watching".
 *
 * @param {Array<Object>} epgList grid channels
 * @param {string|null} selectedCategory chosen category, or null
 * @returns {Array<Object>}
 */
export const filterEpgListByCategory = (epgList, selectedCategory) => {
  if (!Array.isArray(epgList)) {
    return [];
  }

  if (!selectedCategory) {
    return epgList;
  }

  return epgList.filter(
    (item) => item?.category?.toUpperCase() === selectedCategory.toUpperCase(),
  );
};

/**
 * On-air program of an EPG item, if the item has a grid.
 *
 * @param {Object} content content list / EPG item
 * @param {number} [now] reference timestamp
 * @returns {Object|null}
 */
export const getLiveProgram = (content, now = Date.now()) => {
  const programs = Array.isArray(content?.epg) ? content.epg : [];

  return (
    programs.find((program) => {
      if (!program?.startTime || !program?.endTime) {
        return false;
      }

      const startTime = new Date(program.startTime).getTime();
      const endTime = new Date(program.endTime).getTime();

      return (
        Number.isFinite(startTime) &&
        Number.isFinite(endTime) &&
        startTime <= now &&
        now < endTime
      );
    }) || null
  );
};

/**
 * Content of a content list item.
 *
 * Carousel items come wrapped (`{ content, bookmark }`), while EPG grid items
 * arrive flat.
 *
 * @param {Object} item
 * @returns {Object|null}
 */
export const unwrapContent = (item) => item?.content || item || null;

/** Content types the zapping carousel can show. */
const ZAPPABLE_CONTENT_TYPES = [CONTENT_TYPES.BROADCAST, CONTENT_TYPES.EPISODE];

/**
 * Adapts a content list's contents to the format the player's zapping
 * carousel expects.
 *
 * Only BROADCAST channels and episodes are included, and the received order
 * is respected, which is that of the content list configured in the EPG or
 * carousel.
 *
 * @param {Array<Object>} items content list items (wrapped or flat)
 * @param {string} [imageMode] image mode for the card
 * @returns {Array<Object>}
 */
export const mapZappingItems = (items, imageMode = IMAGE_MODE.THUMB) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(unwrapContent)
    .filter(
      (content) =>
        content?.id && ZAPPABLE_CONTENT_TYPES.includes(content.contentType),
    )
    .map((content) => {
      const liveProgram = getLiveProgram(content);
      const image = getContentImageByMode(content.images, imageMode);

      return {
        id: content.id,
        title: content.title || '',
        subtitle: liveProgram?.title || '',
        image: image?.url || '',
        contentType: content.contentType,
        // The original content, so the app can switch channels with
        // all its data (catch up, networks, etc.)
        content,
      };
    });
};
